// Passive demo screens. Pure presentation — no state, no fetches.
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  DEMO_KPIS, DEMO_DEPARTMENTS, DEMO_DAYS, DEMO_GRID, DEMO_PRESENCE,
  DEMO_WORKLOAD, DEMO_KPI_TILES, DEMO_ACTIONS, DEMO_PHONE_DAYS,
  DEMO_TEAM_TODAY, DEMO_CHAT, DEMO_WEEK,
} from './demoData';
import { TrendingUp, TrendingDown, Smartphone, CheckCircle2, AlertTriangle, Clock, Sparkles, Check, ClipboardCheck } from 'lucide-react';

const CHIP = {
  blue:   'bg-sky-100 text-sky-900 border-sky-200',
  indigo: 'bg-indigo-100 text-indigo-900 border-indigo-200',
  teal:   'bg-teal-100 text-teal-900 border-teal-200',
  rose:   'bg-rose-100 text-rose-900 border-rose-200',
  open:   'bg-amber-50 text-amber-800 border-amber-300 border-dashed',
};

const TONE_TEXT = {
  slate: 'text-slate-900', blue: 'text-sky-700', amber: 'text-amber-600', rose: 'text-rose-600',
};

const Panel = ({ title, hint, children, testId }) => (
  <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
    <header className="flex items-baseline justify-between gap-3 border-b border-slate-100 px-4 py-3">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </header>
    <div className="p-4">{children}</div>
  </section>
);

export const DemoDashboard = () => (
  <div className="space-y-5" data-testid="demo-screen-dashboard">
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {DEMO_KPIS.map((k) => (
        <div key={k.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">{k.label}</p>
          <p className={`mt-2 text-3xl font-semibold tabular-nums ${TONE_TEXT[k.tone]}`}>{k.value}</p>
          <p className="mt-1 text-xs text-slate-500">{k.sub}</p>
        </div>
      ))}
    </div>

    <Panel title="Coverage per department" hint={DEMO_WEEK} testId="demo-coverage-panel">
      <div className="space-y-4">
        {DEMO_DEPARTMENTS.map((d) => {
          const pct = Math.round((d.planned / d.required) * 100);
          const bad = pct < 92;
          return (
            <div key={d.name}>
              <div className="mb-1.5 flex items-center justify-between text-xs">
                <span className="font-medium text-slate-700">{d.name}</span>
                <span className="tabular-nums text-slate-500">
                  {d.planned} / {d.required} h · {d.head} people ·{' '}
                  <span className={bad ? 'font-semibold text-rose-600' : 'font-semibold text-emerald-600'}>{pct}%</span>
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${bad ? 'bg-rose-400' : 'bg-teal-500'}`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Panel>

    <div className="grid gap-4 lg:grid-cols-3">
      {[
        { icon: AlertTriangle, tone: 'text-amber-500', title: '6 open slots Friday & Saturday', sub: 'Order Picking — AI suggests 2 fill-ins from Inbound' },
        { icon: Clock, tone: 'text-sky-500', title: 'Overdracht missing for late shift', sub: 'Expedition · nudge sent to the shift lead' },
        { icon: CheckCircle2, tone: 'text-emerald-500', title: 'Week published to 38 phones', sub: 'FlowCrew delivered 38/38 · 2 questions open' },
      ].map((a) => (
        <div key={a.title} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <a.icon className={`mt-0.5 h-5 w-5 shrink-0 ${a.tone}`} />
          <div>
            <p className="text-sm font-medium text-slate-800">{a.title}</p>
            <p className="mt-0.5 text-xs text-slate-500">{a.sub}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const GRID_PHASES = [
  { key: 'idle',      label: 'Workload for the week is in — 12,400 units across four departments',            ms: 1100 },
  { key: 'thinking',  label: 'Calculating required hours · matching availability · applying shift rules',      ms: 2400 },
  { key: 'proposed',  label: 'Proposal ready in 1.8 s — 54 shifts, 2 open slots flagged for the planner',      ms: 1600 },
  { key: 'confirmed', label: 'Planner confirmed and published — 28 phones notified through FlowCrew™',         ms: 3000 },
];

const useGridAutoplay = (total) => {
  const [phase, setPhase] = useState(0);
  const [revealed, setRevealed] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setPhase(3); setRevealed(total); return; }

    let timer;
    const run = (i) => {
      setPhase(i);
      setRevealed(i === 0 ? 0 : total);
      timer = setTimeout(() => run((i + 1) % GRID_PHASES.length), GRID_PHASES[i].ms);
    };
    run(0);
    return () => clearTimeout(timer);
  }, [total]);

  useEffect(() => {
    if (phase !== 1) return;
    setRevealed(0);
    const step = Math.max(24, Math.round(GRID_PHASES[1].ms / (total + 1)));
    const id = setInterval(() => setRevealed((r) => (r >= total ? r : r + 1)), step);
    return () => clearInterval(id);
  }, [phase, total]);

  return { phase, revealed };
};

export const DemoGrid = () => {
  const order = useMemo(() => {
    const map = new Map();
    let n = 0;
    DEMO_GRID.forEach((g) => g.rows.forEach((r) => r.cells.forEach((c, i) => {
      if (c) map.set(`${g.dept}|${r.name}|${i}`, n++);
    })));
    return { map, total: n };
  }, []);

  const { phase, revealed } = useGridAutoplay(order.total);
  const live = GRID_PHASES[phase];
  const confirmed = phase === 3;

  return (
    <Panel title="Week planning · shift grid" hint={DEMO_WEEK} testId="demo-screen-grid">
      <div
        className={`mb-3 flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors duration-500 ${
          confirmed ? 'border-emerald-200 bg-emerald-50/70' : phase === 1 ? 'border-cyan-300 bg-cyan-50/70' : 'border-slate-200 bg-slate-50'
        }`}
        data-testid="demo-grid-autoplay"
      >
        <span
          className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-[11px] font-semibold transition-all duration-300 ${
            confirmed
              ? 'bg-emerald-600 text-white'
              : phase === 2
                ? 'bg-slate-900 text-white'
                : 'bg-cyan-500 text-white' + (phase === 1 ? ' scale-[0.97]' : '')
          }`}
        >
          {confirmed ? <Check className="h-3.5 w-3.5" /> : phase === 2 ? <ClipboardCheck className="h-3.5 w-3.5" /> : <Sparkles className={`h-3.5 w-3.5 ${phase === 1 ? 'animate-spin' : ''}`} />}
          {confirmed ? 'Published' : phase === 2 ? 'Confirm & publish' : 'Suggest staffing'}
        </span>
        <span className="min-w-0 flex-1 text-[11px] leading-relaxed text-slate-600" data-testid="demo-grid-phase">
          {live.label}
        </span>
        <span className="shrink-0 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-400">
          {phase === 1 ? `${revealed}/${order.total}` : 'auto demo'}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-xs">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400">
              <th className="w-48 px-2 py-2">Worker</th>
              {DEMO_DAYS.map((d) => <th key={d} className="px-2 py-2 font-medium">{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {DEMO_GRID.map((group) => (
              <Fragment key={group.dept}>
                <tr className="bg-slate-50">
                  <td colSpan={8} className="px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    {group.dept}
                  </td>
                </tr>
                {group.rows.map((r) => (
                  <tr key={group.dept + r.name} className="border-t border-slate-100">
                    <td className={`px-2 py-2 ${r.open ? 'italic text-amber-700' : 'font-medium text-slate-700'}`}>{r.name}</td>
                    {r.cells.map((c, i) => {
                      const idx = order.map.get(`${group.dept}|${r.name}|${i}`);
                      const shown = c && (phase !== 1 ? phase !== 0 : idx < revealed);
                      return (
                        <td key={i} className="px-1.5 py-1.5">
                          {shown ? (
                            <span
                              className={`block rounded-md border px-2 py-1 text-center text-[11px] font-medium transition-all duration-300 ${CHIP[c.tone]} ${
                                confirmed ? 'ring-1 ring-emerald-300/70' : ''
                              }`}
                            >
                              {c.label}
                            </span>
                          ) : (
                            <span className="block rounded-md border border-dashed border-slate-200 px-2 py-1 text-center text-[11px] text-slate-300">·</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

const STATUS_BADGE = {
  Present:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  Overtime: 'bg-sky-50 text-sky-700 border-sky-200',
  Early:    'bg-slate-50 text-slate-600 border-slate-200',
  Sick:     'bg-amber-50 text-amber-700 border-amber-200',
  'No-show': 'bg-rose-50 text-rose-700 border-rose-200',
};

export const DemoPresence = () => (
  <Panel title="Presence · plan versus actual" hint="Thursday 2 April" testId="demo-screen-presence">
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-xs">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400">
            {['Worker', 'Department', 'Status', 'Planned', 'Actual finish', 'Delta', 'Note'].map((h) => (
              <th key={h} className="px-3 py-2 font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DEMO_PRESENCE.map((r) => (
            <tr key={r.name} className="border-t border-slate-100">
              <td className="px-3 py-2.5 font-medium text-slate-800">{r.name}</td>
              <td className="px-3 py-2.5 text-slate-500">{r.dept}</td>
              <td className="px-3 py-2.5">
                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_BADGE[r.status]}`}>{r.status}</span>
              </td>
              <td className="px-3 py-2.5 tabular-nums text-slate-600">{r.plan}</td>
              <td className="px-3 py-2.5 tabular-nums text-slate-600">{r.actual}</td>
              <td className={`px-3 py-2.5 tabular-nums font-medium ${r.delta.startsWith('+') ? 'text-sky-700' : r.delta.startsWith('−') ? 'text-slate-500' : 'text-slate-400'}`}>{r.delta}</td>
              <td className="px-3 py-2.5 text-slate-500">{r.note || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="mt-3 text-[11px] text-slate-400">
      Overtime and surcharge bands are calculated from the collective-agreement rules per tenant, then exported straight to payroll.
    </p>
  </Panel>
);

const RATE_TONE = { ok: 'text-emerald-600', warn: 'text-amber-600', bad: 'text-rose-600' };

export const DemoWorkload = () => (
  <div className="space-y-5" data-testid="demo-screen-workload">
    <Panel title="Workload in · required hours out" hint={DEMO_WEEK}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-xs">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400">
              {['Department', 'Driver', 'Volume', 'Benchmark', 'Required h', 'Rate needed'].map((h) => (
                <th key={h} className="px-3 py-2 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DEMO_WORKLOAD.map((r) => (
              <tr key={r.dept} className="border-t border-slate-100">
                <td className="px-3 py-2.5 font-medium text-slate-800">{r.dept}</td>
                <td className="px-3 py-2.5 text-slate-500">{r.category}</td>
                <td className="px-3 py-2.5 tabular-nums text-slate-700">{r.volume}</td>
                <td className="px-3 py-2.5 tabular-nums text-slate-500">{r.benchmark}</td>
                <td className="px-3 py-2.5 tabular-nums text-slate-700">{r.hours}</td>
                <td className={`px-3 py-2.5 tabular-nums font-semibold ${RATE_TONE[r.tone]}`}>{r.rate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>

    <Panel title="Balance (Yamazumi) · required versus planned">
      <div className="space-y-4">
        {DEMO_DEPARTMENTS.map((d) => (
          <div key={d.name} className="grid grid-cols-[120px_1fr] items-center gap-3">
            <span className="text-xs font-medium text-slate-700">{d.name}</span>
            <div className="space-y-1.5">
              <div className="h-3 rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-slate-400" style={{ width: `${(d.required / 560) * 100}%` }} />
              </div>
              <div className="h-3 rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-teal-500" style={{ width: `${(d.planned / 560) * 100}%` }} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-4 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-4 rounded-full bg-slate-400" /> required</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-4 rounded-full bg-teal-500" /> planned</span>
      </div>
    </Panel>
  </div>
);

export const DemoImprove = () => (
  <div className="space-y-5" data-testid="demo-screen-improve">
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {DEMO_KPI_TILES.map((k) => (
        <div key={k.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">{k.label}</p>
          <p className="mt-2 flex items-baseline gap-1.5">
            <span className="text-3xl font-semibold tabular-nums text-slate-900">{k.value}</span>
            <span className="text-xs text-slate-400">{k.unit}</span>
          </p>
          <p className={`mt-1 inline-flex items-center gap-1 text-xs font-medium ${k.up ? 'text-emerald-600' : 'text-rose-600'}`}>
            {k.up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
            {k.trend} vs last week
          </p>
        </div>
      ))}
    </div>

    <Panel title="Improvement register" hint="fed automatically from handovers & balance gaps">
      <div className="divide-y divide-slate-100">
        {DEMO_ACTIONS.map((a) => (
          <div key={a.title} className="flex flex-wrap items-center gap-3 py-3">
            <span className="flex-1 min-w-[220px] text-sm text-slate-800">{a.title}</span>
            <span className="text-xs text-slate-500">{a.dept}</span>
            <span className="text-xs text-slate-500">{a.owner}</span>
            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${
              a.priority === 'High' ? 'border-rose-200 bg-rose-50 text-rose-700'
                : a.priority === 'Medium' ? 'border-amber-200 bg-amber-50 text-amber-700'
                : 'border-slate-200 bg-slate-50 text-slate-500'}`}>{a.priority}</span>
            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${
              a.status === 'Closed' ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : a.status === 'In progress' ? 'border-sky-200 bg-sky-50 text-sky-700'
                : 'border-slate-200 bg-slate-50 text-slate-600'}`}>{a.status}</span>
          </div>
        ))}
      </div>
    </Panel>
  </div>
);

const PHONE_TONE = {
  planned: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  holiday: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  free:    'bg-white/[0.03] text-white/30 border-white/10',
};

export const DemoFlowCrew = () => (
  <div className="grid gap-6 lg:grid-cols-[320px_1fr]" data-testid="demo-screen-flowcrew">
    <div className="mx-auto w-full max-w-[320px]">
      <div className="rounded-[2rem] border border-slate-800 bg-[#0b1220] p-3 shadow-2xl">
        <div className="mb-3 flex items-center justify-between px-1">
          <span className="text-[11px] font-semibold text-white/70">My planning</span>
          <Smartphone className="h-3.5 w-3.5 text-white/40" />
        </div>
        <div className="grid grid-cols-7 gap-1">
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <span key={i} className="pb-1 text-center text-[9px] uppercase text-white/30">{d}</span>
          ))}
          {DEMO_PHONE_DAYS.map((d) => (
            <div key={d.d} className={`rounded-md border px-1 py-1.5 text-center ${PHONE_TONE[d.tone]}`}>
              <span className="block text-[10px] font-semibold">{d.d}</span>
              <span className="block text-[8px] leading-tight">{d.tag || '·'}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.03] p-2.5">
          <p className="mb-2 text-[10px] uppercase tracking-wider text-white/40">Team today</p>
          {DEMO_TEAM_TODAY.map((m) => (
            <div key={m.name} className="flex items-center justify-between py-0.5 text-[11px]">
              <span className={m.me ? 'font-semibold text-cyan-300' : 'text-white/70'}>
                {m.name}{m.me && ' · you'}
              </span>
              <span className="text-white/40">{m.shift}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-2">
          {DEMO_CHAT.map((c, i) => (
            <p
              key={i}
              className={`max-w-[85%] rounded-xl px-2.5 py-1.5 text-[11px] leading-snug ${
                c.from === 'worker'
                  ? 'ml-auto bg-cyan-500/20 text-cyan-100'
                  : 'bg-white/[0.06] text-white/70'}`}
            >
              {c.text}
            </p>
          ))}
        </div>
      </div>
    </div>

    <div className="space-y-4">
      <Panel title="FlowCrew™ — the crew's half of the platform" hint="live on Google Play · iOS under consideration">
        <ul className="space-y-3 text-sm text-slate-600">
          {[
            'The moment a planner publishes the week, it is on every phone — colour-coded per day, in the worker\'s own language.',
            'Nine languages: EN · NL · PL · RO · BG · TR · ES · PT · UA.',
            'One tap on a day opens a chat with the planner about that exact shift — no more "which Friday?".',
            'Day off, holiday range or shift swap requests land in the planner\'s inbox and bounce straight back with a decision.',
            'Team view shows who is in today, with a "you" marker on the worker\'s own row.',
          ].map((l) => (
            <li key={l} className="flex gap-2.5">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-500" />
              <span>{l}</span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  </div>
);
